import {UIWRoomUserListController} from './UIWRoomUserListController.ts'
import {UIWRoomCenterController} from './UIWRoomCenterController.ts'
import {type RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {type UIManager} from '../UIManager.ts'
import {Scene} from '../UISceneTypes.ts'
import {UIRoomChatController} from './UIRoomChatController.ts'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables.ts'
import {PIXI} from '@colonist/pixi'
import {removeHash} from '../../../../shared/scripts/ui_utils/Utils.ts'
import {type FriendStatusData} from '../../../../../shared/friends/FriendTypes.ts'
import {type BigId, LobbyImage, type UserSessionId} from '@colonist/types'
import {UIRoomFriendListController} from '../../../../shared/scripts/ui_html/room/UIRoomFriendListController.ts'
import {UIRoomPlayOrderSelectionController} from './play_order_selection/UIRoomPlayOrderSelectionController.ts'
import {type RoomPlayOrderSelectionData, type RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {UIDRoomFriendListController} from './UIDRoomFriendListController.ts'
import {UIRoomSettingsController} from '../../../../shared/scripts/ui_room/UIRoomSettingsController.ts'
import {notificationController, socket} from '../../../../shared/scripts/EntryShared.ts'
import {RoomStateChangerNotifier} from '../../../../shared/scripts/RoomStateChangerNotifier.ts'
import {UIRoomExitConfirmationViewPopup} from '../../../../shared/scripts/ui_html/room/UIRoomExitConfirmationViewPopup.ts'
import {UINotificationSendHelper} from '../../../../shared/scripts/notifications/UINotificationSendHelper.ts'

export class UIRoomController {
    private readonly container: HTMLDivElement
    private readonly userList: UIWRoomUserListController
    private readonly friendList: UIRoomFriendListController
    readonly chat: UIRoomChatController
    center: UIWRoomCenterController

    private roomInitiated: boolean = false
    private readonly uiManager: UIManager
    private playOrderSelectionController: UIRoomPlayOrderSelectionController
    private roomState: RoomClientState | undefined

    constructor(uiManager: UIManager) {
        this.uiManager = uiManager
        this.container = document.getElementById('scene_room_container') as HTMLDivElement

        this.userList = new UIWRoomUserListController()
        this.chat = new UIRoomChatController()
        this.center = new UIWRoomCenterController()

        const friendListContainer = document.getElementById('scene_room_friend_list') as HTMLDivElement
        const friendHeader = document.getElementById('scene_room_left_friend_header') as HTMLElement
        const friendPlayerList = document.getElementById('scene_room_player_list') as HTMLDivElement
        this.friendList = UIUtilsEjsVariables.isUserOnDiscord ? new UIDRoomFriendListController(friendListContainer, friendHeader, friendPlayerList) : new UIRoomFriendListController(friendListContainer, friendHeader, friendPlayerList)

        this.updatePageOnHashChange()
    }

    hide() {
        this.container.style.display = 'none'
        this.userList.clear()
    }

    show() {
        this.container.style.display = 'flex'
        if(this.roomState == undefined) return
        this.userList.load(this.roomState)
        this.center.load(this.roomState)
        this.friendList?.updateRoomState(this.roomState)
    }

    updateRoom(roomState: RoomClientState) {
        if((this.roomState != undefined) && (roomState.updateSequence < this.roomState.updateSequence)) return

        if(socket?.lobbyListener != undefined) socket.lobbyListener.room = roomState // TODO remove when socket.lobbyListener.room is not used anymore
        this.refreshBrowserIfCurrentSceneIsGame()
        if(this.roomInitiated) {
            this.updateCurrentRoom(roomState)
            return
        }
        this.initRoom(roomState)
    }

    updateSeedOrderSelection(data: RoomSeedOrderSelectionData) {
        if(this.roomState == undefined) return
        this.center.updateSeedOrderSelection(this.roomState, data)
        this.playOrderSelectionController.seedOrder = data
        this.center.update(undefined, this.roomState)
    }

    updatePlayOrderSelection(data: RoomPlayOrderSelectionData) {
        if(this.roomState == undefined) return
        this.playOrderSelectionController.update(this.roomState, data)
    }

    leaveRoom(toScene?: Scene, data?: Record<string, unknown>, forceLeave: boolean = false) {
        toScene ??= Scene.Lobby

        if(this.roomState?.isMultiplayer() && !forceLeave) {
            new UIRoomExitConfirmationViewPopup(() => {
                this.resetRoom()
                this.uiManager.sceneController.loadScene(toScene, data)
            })
            return
        }

        this.resetRoom()
        this.uiManager.sceneController.loadScene(toScene, data)
    }

    onKickedInactiveHost() {
        if(this.roomState?.isHost(socket.controller.session.id)) {
            this.chat.post({
                icon: LobbyImage.Lightning,
                text: {key: 'strings:roomPage.notifications.kickedDueToInactivity'},
            })
        }
        notificationController.showNotification(LobbyImage.Lightning, {key: 'strings:roomPage.notifications.kickingInactiveHost'})
    }

    resetRoom() {
        socket.roomClient.leave()
        this.roomInitiated = false
        this.roomState = undefined
        this.userList.clear()
        this.chat.clear()
        removeHash()
    }

    private initRoom(roomState: RoomClientState) {
        UIRoomController.updateURLToRoom(roomState.gameSetting.id)
        this.roomInitiated = true
        this.roomState = roomState

        this.friendList.setRoomId(roomState.gameSetting.id)

        UIRoomSettingsController.initRoomSettingFromLocalStorage(roomState.gameSetting.modeSetting)
        this.playOrderSelectionController = new UIRoomPlayOrderSelectionController()
        this.uiManager.sceneController.loadScene(Scene.Room)

        this.friendList?.animateInviteFriendsButton()

        UINotificationSendHelper.registerToNotificationServiceAndGetNotifications()
    }

    private updateCurrentRoom(newRoomState: RoomClientState) {
        this.userList.update(newRoomState)
        this.friendList?.updateRoomState(newRoomState)
        this.center.update(this.roomState, newRoomState)
        RoomStateChangerNotifier.run(this.roomState, newRoomState)

        this.roomState = newRoomState
    }

    private refreshBrowserIfCurrentSceneIsGame() {
        const isUserInGame = this.uiManager.sceneController.scene == Scene.Game
        if(!isUserInGame) return
        location.reload()
    }

    static updateURLToRoom(gameId: string) {
        if(window.location.hash == '#' + gameId) return
        window.location.hash = gameId
    }

    private updatePageOnHashChange() {
        window.onhashchange = (hashChangeEvent: HashChangeEvent) => {
            const oldUrl = new URL(hashChangeEvent.oldURL)
            const newUrl = new URL(hashChangeEvent.newURL)

            const adHash = '#goog_rewarded'
            if(newUrl.hash == adHash || oldUrl.hash == adHash) {
                history.replaceState(null, document.title, window.location.pathname + window.location.search)
                return
            }

            const isTheSameHash = oldUrl.hash.toLocaleLowerCase() == newUrl.hash.toLocaleLowerCase()
            if(isTheSameHash) return

            const hashesNotEmpty = oldUrl.hash != '' && newUrl.hash != ''

            if(hashesNotEmpty) {
                location.reload()
                return
            }

            const sceneIsRoom = this.uiManager.sceneController.scene == Scene.Room
            const sceneIsGame = this.uiManager.sceneController.scene == Scene.Game
            const roomAlreadyInitiated = sceneIsRoom && this.roomInitiated

            const newHashIsSet = newUrl.hash != ''

            if(roomAlreadyInitiated) return
            if(sceneIsGame && newHashIsSet) return

            location.reload()
        }
    }

    static showHeaderInRoom(): boolean {
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return true
        if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return false
        if(PIXI.utils.isMobile.phone) return false
        return true
    }

    updateAllFriendsOnlineStatus(friends: FriendStatusData[]) {
        this.friendList?.updateFriendsList(friends)
    }

    updateFriendOnlineStatus(friend: FriendStatusData) {
        this.friendList?.updateFriendOnlineStatus(friend)
    }

    toggleFriendInviteButtonState(responderUserId: BigId, enable: boolean) {
        this.friendList?.toggleFriendInviteButtonState(responderUserId, enable)
    }

    getSessionByUserSessionId(userSessionId: UserSessionId) {
        return this.roomState?.getSessions().find((session) => session.userSessionId == userSessionId)
    }
}
